import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { getRentalPortalApplicant } from '../../../api'
import {
  getPageBrowserTabTitleWithEntityName,
  usePageBrowserTabTitle,
} from '../../../hooks'
import { EmailDraftProvider } from '../../Email'
import { useAsyncEntity } from '../../Entity'
import { EntityPageProfileLayout } from '../../Page'
import { toApplicantDetail } from '../../RentalPortalApplicant'

import { QA_NAME_APPLICANT_PROFILE_PAGE } from './RentalPortalApplicantProfile.constants'
import { ApplicantProfileContext } from './RentalPortalApplicantProfile.context'
import {
  getApplicantProfileTitle,
  getHasApplicantProfileLoadFailed,
  getIsApplicantProfileNotFound,
} from './RentalPortalApplicantProfile.utils'
import { APPLICANT_PROFILE_EMAILS_DRAFT_PREFIX } from './RentalPortalApplicantProfileEmails'
import { RentalPortalApplicantProfileHeader } from './RentalPortalApplicantProfileHeader'
import { RentalPortalApplicantProfileLoadState } from './RentalPortalApplicantProfileLoadState'
import { RentalPortalApplicantProfileTabs } from './RentalPortalApplicantProfileTabs'
import { RentalPortalApplicantProfileWidgetList } from './RentalPortalApplicantProfileWidgetList'

function RentalPortalApplicantProfile() {
  const { applicantId } = useParams()
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  async function getProfileData() {
    return getRentalPortalApplicant(applicantId).then(toApplicantDetail)
  }

  const {
    entityData,
    handleEntityInit,
    handleEntityReload,
    handleEntitySilentReload,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({ entityId: applicantId, getProfileData })

  function handleCloseModal() {
    setModal(null)
  }

  usePageBrowserTabTitle({
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: getApplicantProfileTitle({ applicantData: entityData, t }),
      title: t('RentalPortal.ApplicantProfileBrowserTabTitleKey'),
    }),
  })

  const hasLoadFailed = getHasApplicantProfileLoadFailed({
    applicantData: entityData,
    isInitiated,
    serverError,
  })

  function handleClickRetry() {
    handleEntityInit()
  }

  return (
    <ApplicantProfileContext
      value={{
        applicantData: entityData,
        applicantId,
        handleCloseModal,
        handleEntityReload,
        handleEntitySilentReload,
        isInitiated,
        isLoading,
        setModal,
      }}
    >
      <EmailDraftProvider
        prefix={`${APPLICANT_PROFILE_EMAILS_DRAFT_PREFIX}-${applicantId}`}
      >
        <EntityPageProfileLayout
          Header={hasLoadFailed ? null : RentalPortalApplicantProfileHeader}
          qaName={QA_NAME_APPLICANT_PROFILE_PAGE}
          widgets={
            hasLoadFailed ? null : <RentalPortalApplicantProfileWidgetList />
          }
        >
          {hasLoadFailed ? (
            <RentalPortalApplicantProfileLoadState
              isNotFound={getIsApplicantProfileNotFound(serverError)}
              onClickRetry={handleClickRetry}
            />
          ) : (
            <RentalPortalApplicantProfileTabs />
          )}
          {modal}
        </EntityPageProfileLayout>
      </EmailDraftProvider>
    </ApplicantProfileContext>
  )
}

export default RentalPortalApplicantProfile
